import { GenericChartCard } from '@components/ui/Chart';
import { capSeries } from '@components/ui/Chart/capSeries';
import type {
    ChartSeries,
    ResultChartPayload,
} from '@diagon-alley/common';
import type { MetricSeriesConfig } from '@hooks/graph/useMetricChart';
import { Button, Card } from '@mantine/core';
import { useMemo, useState } from 'react';

import styles from './ReportsContainer.module.css';

interface ResultChartCardProps {
    payload: ResultChartPayload;
}

type ChartRow = Record<string, unknown>;

/**
 * Renders an inline showResultChart payload through the existing
 * GenericChartCard (Recharts under the hood).
 *
 * - Maps the shared ChartSeries schema to MetricSeriesConfig.
 * - dimensionType=category renders the period strings as discrete
 *   categories (works with our normalized yyyy-MM-dd keys without
 *   extra parsing).
 */
export default function ResultChartCard({ payload }: ResultChartCardProps) {
    const allSeries = useMemo<MetricSeriesConfig<ChartRow>[]>(
        () => payload.series.map(toMetricSeriesConfig),
        [payload.series],
    );
    // Many series make an unreadable chart: show the largest few and fold
    // the rest into "Other" until the person asks for all of them.
    const [showAll, setShowAll] = useState(false);
    const capped = useMemo(
        () => capSeries(payload.data as ChartRow[], allSeries),
        [payload.data, allSeries],
    );
    const folded = capped.folded.length > 0;
    const data = showAll || !folded ? (payload.data as ChartRow[]) : capped.data;
    const series = showAll || !folded ? allSeries : capped.series;

    return (
        <Card withBorder radius="md" className={styles.tableCard} p={0}>
            <GenericChartCard<ChartRow>
                // The wrapper keeps each series' shown/hidden state from
                // first render; a fresh key gives the other set a clean start.
                key={showAll ? 'all' : 'top'}
                kind={payload.kind === 'pie' ? 'pie' : payload.kind}
                title={payload.title}
                subtitle={payload.subtitle}
                data={data}
                dimensionKey={payload.dimensionKey as keyof ChartRow}
                dimensionType="category"
                series={series}
                height={280}
                controls={{
                    showRange: false,
                    showSeriesToggles: series.length > 1,
                    showToolbar: Boolean(payload.title) || folded,
                }}
                rightSlot={
                    folded ? (
                        <Button
                            variant="subtle"
                            size="compact-xs"
                            color="gray"
                            onClick={() => setShowAll((value) => !value)}
                        >
                            {showAll
                                ? `Show top ${capped.series.length - 1}`
                                : `Show all ${allSeries.length}`}
                        </Button>
                    ) : undefined
                }
            />
        </Card>
    );
}

function toMetricSeriesConfig(
    s: ChartSeries,
    idx: number,
): MetricSeriesConfig<ChartRow> {
    return {
        id: s.key || `series-${idx}`,
        key: s.key as keyof ChartRow,
        label: s.label,
        color: s.color,
        renderAs: s.renderAs ?? 'line',
    };
}
